import type { MakeOptional } from '@/api/sanity/generated';
import { Product } from '@/api/skippay/gcssProducts';
import { Badge } from '@/components/Badge/Badge';
import { Link } from '@/components/links';
import { formatApiAmount } from '@/utils/amount';
import { Currency, formatAmount } from '@/utils/formatting';
import Image from 'next/image';
import { logProductsEvent } from '@/analytics/logProductsEvent';

export type SimpleProduct = MakeOptional<
	Product,
	'availability' | 'categoryId' | 'paginationId' | 'description' | 'merchantName'
>;

interface Props {
	product: SimpleProduct | undefined;
	category: string | undefined;
	subcategory: string | undefined;
	page: number | undefined;
}

export function ProductCard(props: Props) {
	const { product, category, subcategory, page } = props;

	const imagePlaceholder = '/images/sections/gcss/product-placeholder.png';

	if (!product)
		return (
			<div className="shadow-card flex animate-pulse flex-col items-start rounded-xl p-4 text-gray-900 no-underline">
				<div className="mx-auto mb-4 h-52 max-h-52 w-full rounded-lg bg-slate-200" />
				<Badge title="&nbsp;" className="w-40 max-w-full bg-slate-200" />
				<div className="mt-3 mb-4 flex w-full flex-col gap-2">
					<div className="h-5 rounded-sm bg-slate-200"></div>
					<div className="h-5 rounded-sm bg-slate-200"></div>
				</div>
				<div className="h-5 w-20 max-w-full bg-slate-200"></div>
			</div>
		);

	const image = product.imageLink || imagePlaceholder;

	function onClick() {
		if (product) {
			logProductsEvent([product], category, subcategory, page, 'select_item');
		}
	}

	return (
		<Link
			analyticsParam={`gcssProductCard${product.id}`}
			testId={`gcssProductCard${product.id}`}
			to={product.merchantLink}
			onClick={onClick}
			className="shadow-card flex flex-col items-start rounded-xl bg-white p-4 text-gray-900 no-underline transition-colors duration-200 ease-in-out hover:text-gray-900"
			target="_blank"
		>
			<Image
				className="mx-auto mb-4 max-h-52 overflow-hidden rounded-lg object-contain"
				width={305}
				height={200}
				alt={product.title}
				src={image}
			/>

			{product.merchantName && <Badge title={product.merchantName} />}

			<h3 className="mt-3 mb-4 line-clamp-2 text-sm font-semibold md:text-base">{product.title}</h3>

			<p className="mt-auto font-bold">
				{formatAmount(
					formatApiAmount(product.priceAmount),
					undefined,
					product.priceCurrency as Currency
				)}
			</p>
		</Link>
	);
}
